import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { backfillGmailIntegration } from "./schema/backfill-gmail-integration";
import { createSupportAiAndBaseSubscription } from "./schema/billing/create-support-ai-and-base-subscription";
import { createSupportAiSubscription } from "./schema/billing/create-support-ai-subscription";
import { createSupportBaseSubscription } from "./schema/billing/create-support-base-subscription";
import { createSupportPhoneSubscription } from "./schema/billing/create-support-phone-subscription";
import { getPreferredPaymentProvider } from "./schema/billing/get-preferred-payment-provider";
import { checkMetaDeletionStatus } from "./schema/check-meta-deletion-status";
import { configureMerchantEmail } from "./schema/email/configure-merchant-email";
import { deleteSenderEmail } from "./schema/email/delete-sender-email";
import { getMerchantDnsValues } from "./schema/email/get-merchant-dns-values";
import { setDefaultEmail } from "./schema/email/set-default-email";
import { updateSenderEmail } from "./schema/email/update-sender-email";
import { verifyMerchantEmail } from "./schema/email/verify-merchant-email";
import { generateCopilotResponse } from "./schema/generate-copilot-response";
import { getBaseSupportPredictedPriceInfo } from "./schema/get-base-support-predicted-price-info";
import { getShopperAiDashboardMetrics } from "./schema/get-concierge-dashboard-metrics";
import { getConversationInsightsAnalytics } from "./schema/get-conversation-insights-analytics";
import { getConversationTopics } from "./schema/get-conversation-topics";
import { getCustomerSuggestions } from "./schema/get-customer-suggestions";
import { getLlmThread } from "./schema/get-llm-thread";
import { getShopperAiWrapped } from "./schema/get-shopper-ai-wrapped";
import { connectGorgiasPrivateApp } from "./schema/integrations/connect-gorgias-private-app";
import { connectRichpanelPrivateApp } from "./schema/integrations/connect-richpanel-private-app";
import { disconnectGorgiasIntegration } from "./schema/integrations/disconnect-gorgias-integration";
import { disconnectRichpanelIntegration } from "./schema/integrations/disconnect-richpanel-integration";
import { enableGorgiasEscalation } from "./schema/integrations/enable-gorgias-escalation";
import { getGorgiasConnectionStatus } from "./schema/integrations/get-gorgias-connection-status";
import { getRechargeIntegration } from "./schema/integrations/get-recharge-integration";
import { getRichpanelConnectionStatus } from "./schema/integrations/get-richpanel-connection-status";
import { updateGorgiasSmsNumber } from "./schema/integrations/update-gorgias-sms-number";
import { updateRichpanelSmsNumber } from "./schema/integrations/update-richpanel-sms-number";
import { upsertRechargeIntegration } from "./schema/integrations/upsert-recharge-integration";
import { lookupProductsByIds } from "./schema/lookup-products-by-ids";
import { markConversationsSpam } from "./schema/mark-conversations-spam";
import { regenerateConversationInsights } from "./schema/regenerate-conversation-insights";
import { submitContactForm } from "./schema/submit-contact-form";
import { cancelSubscription } from "./schema/subscriptions/cancel-subscription";
import { editSubscription } from "./schema/subscriptions/edit-subscription";
import { editSubscriptionAddress } from "./schema/subscriptions/edit-subscription-address";
import { getCustomerSubscriptions } from "./schema/subscriptions/get-customer-subscriptions";
import { getCustomerSubscriptionTotalSpent } from "./schema/subscriptions/get-customer-total-spent";
import { skipSubscription } from "./schema/subscriptions/skip-subscription";
import { supportAnalytics } from "./schema/support-analytics";
import { createSupportFolder } from "./schema/support-folders/create-support-folder";
import { deleteSupportFolder } from "./schema/support-folders/delete-support-folder";
import { getSupportFolderById } from "./schema/support-folders/get-support-folder-by-id";
import { getSupportFolders } from "./schema/support-folders/get-support-folders";
import { updateSupportFolder } from "./schema/support-folders/update-support-folder";
import { supportFrontendAgent } from "./schema/support-frontend-agent";
import { updateGmailIntegrationName } from "./schema/update-gmail-integration-name";

const billingEndpoints = {
  createSupportAiSubscription,
  createSupportBaseSubscription,
  createSupportPhoneSubscription,
  createSupportAiAndBaseSubscription,
  getPreferredPaymentProvider,
};

const emailEndpoints = {
  configureMerchantEmail,
  verifyMerchantEmail,
  getMerchantDnsValues,
  updateSenderEmail,
  deleteSenderEmail,
  setDefaultEmail,
};

const integrationEndpoints = {
  getRechargeIntegration,
  upsertRechargeIntegration,
  connectGorgiasPrivateApp,
  disconnectGorgiasIntegration,
  enableGorgiasEscalation,
  getGorgiasConnectionStatus,
  updateGorgiasSmsNumber,
  connectRichpanelPrivateApp,
  disconnectRichpanelIntegration,
  getRichpanelConnectionStatus,
  updateRichpanelSmsNumber,
};

const subscriptionEndpoints = {
  getCustomerSubscriptions,
  editSubscription,
  editSubscriptionAddress,
  cancelSubscription,
  skipSubscription,
  getCustomerSubscriptionTotalSpent,
};

const supportFolderEndpoints = {
  createSupportFolder,
  updateSupportFolder,
  deleteSupportFolder,
  getSupportFolders,
  getSupportFolderById,
};

export const rpcDefinition = {
  checkMetaDeletionStatus,
  generateCopilotResponse,
  getBaseSupportPredictedPriceInfo,
  getConversationInsightsAnalytics,
  getConversationTopics,
  getCustomerSuggestions,
  getLlmThread,
  getShopperAiDashboardMetrics,
  getShopperAiWrapped,
  lookupProductsByIds,
  markConversationsSpam,
  regenerateConversationInsights,
  submitContactForm,
  supportAnalytics,
  supportFrontendAgent,
  backfillGmailIntegration,
  updateGmailIntegrationName,
  ...billingEndpoints,
  ...emailEndpoints,
  ...integrationEndpoints,
  ...subscriptionEndpoints,
  ...supportFolderEndpoints,
};

export type RedoSupportRpcDefinition = InferRpcDefinition<typeof rpcDefinition>;
